import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { styled } from '@linaria/react'
import { MinimalAlert, MinimalButton, MinimalCard, MinimalInput } from '@ovasabi/ui-minimal'
import { minimalVars } from '@ovasabi/ui-minimal/tokens'
import type { TenantProjectionStore } from '@ovasabi/frontend-kit'
import { CityPicker, PageHeader, Screen } from '../../components/chow'
import { CameraIcon, CheckIcon, PinIcon } from '../../components/icons'
import { prototypeStoreRegistry, useVendorProfileSnapshot } from '../../generated/prototypeRuntime'
import { IMAGE_ACCEPT, type ImageAdvisory } from '../../lib/imageProcessing'
import { normalizePhone } from '../../lib/phone'
import { commandPayload, commands, isLiveRuntime, mediaUrl } from '../../lib/runtime'
import { useChooseChow } from './ChooseChowContext'
import { useAuth } from './auth/AuthContext'
import { saveChefPhoto } from './chefPhoto'
import { PhotoAdvice } from './imagePicker'
import { useVendorLocations } from './projections'

type StepKey = 'location' | 'photo' | 'phone'

const Style = {
  Wrap: styled.div`
    display: grid;
    gap: ${minimalVars.space.md};
    width: 100%;
    max-width: 480px;
    margin: 0 auto;
  `,
  Progress: styled.div`
    display: grid;
    grid-auto-flow: column;
    grid-auto-columns: 1fr;
    gap: 6px;
  `,
  Bar: styled.span<{ $on: boolean }>`
    height: 4px;
    border-radius: 999px;
    background: ${({ $on }) => ($on ? minimalVars.color.brand : minimalVars.color.borderSubtle)};
    transition: background 0.2s ease;
  `,
  Count: styled.p`
    margin: 0;
    color: ${minimalVars.color.textTertiary};
    font-size: ${minimalVars.typography.captionSize};
  `,
  Title: styled.h2`
    margin: 0;
    font-family: ${minimalVars.typography.displayFamily};
    font-size: 1.35rem;
    line-height: 1.2;
    color: ${minimalVars.color.textPrimary};
  `,
  Body: styled.p`
    margin: 0;
    color: ${minimalVars.color.textSecondary};
    line-height: 1.5;
  `,
  Fields: styled.div`
    display: grid;
    gap: ${minimalVars.space.sm};
  `,
  Pair: styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: ${minimalVars.space.sm};
  `,
  Actions: styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: ${minimalVars.space.xs};
    justify-content: space-between;
    align-items: center;
  `,
  Photo: styled.button`
    display: grid;
    place-items: center;
    width: 132px;
    height: 132px;
    margin: 0 auto;
    border-radius: 50%;
    border: 2px dashed ${minimalVars.color.borderStrong};
    background: ${minimalVars.color.bgSurfaceAlt};
    color: ${minimalVars.color.textTertiary};
    cursor: pointer;
    overflow: hidden;
    padding: 0;
    img { width: 100%; height: 100%; object-fit: cover; }
  `,
  Icon: styled.span`
    display: inline-grid;
    place-items: center;
    width: 44px;
    height: 44px;
    border-radius: 14px;
    background: color-mix(in srgb, ${minimalVars.color.brand} 10%, transparent);
    color: ${minimalVars.color.brand};
  `,
}

/**
 * First-run setup for a new kitchen, shown straight after a chef signs up.
 *
 * It asks only for what is still missing (where they cook, a photo and a phone
 * number) one question at a time, and every step can be skipped: the dashboard
 * checklist keeps whatever is left. The plan is fixed when the page loads, so
 * finishing a step never reshuffles the ones ahead.
 */
export const ChefWelcomePage = () => {
  const navigate = useNavigate()
  const { profileId } = useChooseChow()
  const { user, updateProfile } = useAuth()
  const locations = useVendorLocations(profileId)
  const vendors = useVendorProfileSnapshot() as { records: readonly Record<string, unknown>[] }
  const vendor = useMemo(
    () => vendors.records.find((v) => String(v.profile_id ?? '') === profileId),
    [vendors.records, profileId],
  )
  const live = isLiveRuntime()

  const [plan, setPlan] = useState<StepKey[] | null>(null)
  const [index, setIndex] = useState(0)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [address, setAddress] = useState('')
  const [area, setArea] = useState('')
  const [city, setCity] = useState('')
  const [cityRef, setCityRef] = useState('')
  const [phone, setPhone] = useState('')
  const [photoUrl, setPhotoUrl] = useState('')
  const [advisories, setAdvisories] = useState<ImageAdvisory[]>([])
  const fileRef = useRef<HTMLInputElement>(null)

  // Work out once what this chef still needs. Locations and the phone number
  // are read from the server so a chef returning here is not asked again.
  useEffect(() => {
    if (plan || !profileId) return
    let active = true
    const knownLocations = live
      ? commands.profile
          .listVendorLocations(profileId)
          .then((response) => {
            const rows = commandPayload(response).locations
            return Array.isArray(rows) ? rows.length > 0 : locations.length > 0
          })
          .catch(() => locations.length > 0)
      : Promise.resolve(locations.length > 0)
    const knownPhone = user?.phone
      ? Promise.resolve(true)
      : live
        ? commands.profile
            .getProfile(profileId)
            .then((response) => Boolean(String(commandPayload(response).phone ?? '').trim()))
            .catch(() => false)
        : Promise.resolve(false)
    void Promise.all([knownLocations, knownPhone]).then(([hasLocation, hasPhone]) => {
      if (!active) return
      const hasPhoto = Boolean(String(vendor?.avatar_image_url ?? user?.avatarUrl ?? '').trim())
      const next: StepKey[] = []
      if (!hasLocation) next.push('location')
      if (!hasPhoto) next.push('photo')
      if (!hasPhone) next.push('phone')
      if (next.length === 0) {
        navigate('/dashboard', { replace: true })
        return
      }
      setPlan(next)
    })
    return () => {
      active = false
    }
    // Only the first resolution matters; later store updates must not rebuild the plan.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileId])

  const advance = () => {
    setError('')
    setIndex((value) => value + 1)
  }

  const saveLocation = async () => {
    if (!address.trim() || !area.trim() || !cityRef || busy) return
    setBusy(true)
    setError('')
    const payload = {
      label: `${area.trim()} kitchen`,
      address: address.trim(),
      area: area.trim(),
      city: city.trim(),
      city_ref: cityRef,
      is_primary: true,
    }
    try {
      let saved: Record<string, unknown> = { id: `local-loc-${Date.now()}`, profile_id: profileId, ...payload }
      if (live) {
        saved = commandPayload(await commands.profile.setVendorLocation(profileId, payload))
        if (!saved.id) throw new Error('Location was not confirmed')
      }
      const store = prototypeStoreRegistry.get('profile.vendor_locations') as unknown as
        | TenantProjectionStore<Record<string, unknown>>
        | undefined
      store?.upsert({ profile_id: profileId, ...saved })
      advance()
    } catch {
      setError('We couldn’t save your address. Check your connection and try again.')
    } finally {
      setBusy(false)
    }
  }

  const pickPhoto = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file || busy) return
    setBusy(true)
    setError('')
    try {
      setAdvisories([])
      const saved = await saveChefPhoto({
        file,
        profileId,
        fallbackOwner: user?.id ?? '',
        vendor,
        displayName: user?.displayName ?? '',
        updateProfile,
      })
      setPhotoUrl(saved.url)
      setAdvisories(saved.advisories)
    } catch (err) {
      setError(
        err instanceof Error && /too large/i.test(err.message)
          ? err.message
          : 'We couldn’t upload that photo. Try another one.',
      )
    } finally {
      setBusy(false)
    }
  }

  const savePhone = async () => {
    const normalized = normalizePhone(phone)
    if (!normalized) {
      setError('Enter a full phone number, for example 0803 123 4567.')
      return
    }
    setBusy(true)
    setError('')
    try {
      await updateProfile({ phone: normalized })
      advance()
    } catch {
      setError('We couldn’t save that number. It may already be on another account.')
    } finally {
      setBusy(false)
    }
  }

  const header = <PageHeader title="Set up your kitchen" accentDot />

  if (!plan) {
    return (
      <Screen header={header}>
        <Style.Wrap>
          <Style.Count>Getting your kitchen ready…</Style.Count>
        </Style.Wrap>
      </Screen>
    )
  }

  const step = plan[index]
  const total = plan.length

  if (!step) {
    return (
      <Screen header={header}>
        <Style.Wrap>
          <MinimalCard padding="md" enter={false}>
            <Style.Fields>
              <Style.Icon aria-hidden="true">
                <CheckIcon size="md" />
              </Style.Icon>
              <Style.Title>Now add your first dish</Style.Title>
              <Style.Body>
                Customers can order once you add a dish and publish a menu. Start with the dish people already ask you for.
              </Style.Body>
              <Style.Actions>
                <MinimalButton variant="ghost" onClick={() => navigate('/dashboard', { replace: true })}>
                  Go to my kitchen
                </MinimalButton>
                <MinimalButton onClick={() => navigate('/menu?view=library', { replace: true })}>Add a dish</MinimalButton>
              </Style.Actions>
            </Style.Fields>
          </MinimalCard>
        </Style.Wrap>
      </Screen>
    )
  }

  const skip = (
    <MinimalButton variant="ghost" disabled={busy} onClick={advance}>
      Skip for now
    </MinimalButton>
  )

  return (
    <Screen header={header}>
      <Style.Wrap>
        <Style.Progress aria-hidden="true">
          {plan.map((key, i) => (
            <Style.Bar key={key} $on={i <= index} />
          ))}
        </Style.Progress>
        <Style.Count>
          Step {index + 1} of {total}
        </Style.Count>

        <MinimalCard padding="md" enter={false}>
          {step === 'location' && (
            <Style.Fields>
              <Style.Icon aria-hidden="true">
                <PinIcon size={22} />
              </Style.Icon>
              <Style.Title>Where do you cook?</Style.Title>
              <Style.Body>Customers collect their orders here, and people nearby find you by your area.</Style.Body>
              <MinimalInput label="Street address" placeholder="For example, 12 Admiralty Way" value={address}
                onChange={(e) => setAddress(e.target.value)} autoComplete="street-address" />
              <Style.Pair>
                <MinimalInput label="Area" placeholder="Lekki Phase 1" value={area} onChange={(e) => setArea(e.target.value)} />
                <CityPicker value={cityRef} onChange={(picked) => {
                  setCityRef(picked.ref)
                  setCity(picked.name)
                }} />
              </Style.Pair>
              {error && <MinimalAlert tone="danger">{error}</MinimalAlert>}
              <Style.Actions>
                {skip}
                <MinimalButton onClick={saveLocation} disabled={busy || !address.trim() || !area.trim() || !cityRef}>
                  {busy ? 'Saving…' : 'Save and continue'}
                </MinimalButton>
              </Style.Actions>
            </Style.Fields>
          )}

          {step === 'photo' && (
            <Style.Fields>
              <Style.Title>Add a photo</Style.Title>
              <Style.Body>
                People order more readily from a kitchen they can picture. A clear photo of you or your best dish works well.
              </Style.Body>
              <input ref={fileRef} type="file" accept={IMAGE_ACCEPT} hidden onChange={pickPhoto} />
              <Style.Photo type="button" onClick={() => fileRef.current?.click()} disabled={busy}
                aria-label={photoUrl ? 'Change photo' : 'Choose a photo'}>
                {photoUrl ? <img src={mediaUrl(photoUrl, 384)} alt="" /> : <CameraIcon size={32} />}
              </Style.Photo>
              {!busy && <PhotoAdvice advisories={advisories} />}
              {error && <MinimalAlert tone="danger">{error}</MinimalAlert>}
              <Style.Actions>
                {skip}
                {photoUrl ? (
                  <MinimalButton onClick={advance}>Continue</MinimalButton>
                ) : (
                  <MinimalButton onClick={() => fileRef.current?.click()} disabled={busy}>
                    {busy ? 'Uploading…' : 'Choose a photo'}
                  </MinimalButton>
                )}
              </Style.Actions>
            </Style.Fields>
          )}

          {step === 'phone' && (
            <Style.Fields>
              <Style.Title>Your phone number</Style.Title>
              <Style.Body>So we can reach you quickly about an order. You can also sign in with it.</Style.Body>
              <MinimalInput label="Phone number" type="tel" placeholder="0803 123 4567" value={phone}
                onChange={(e) => setPhone(e.target.value)} autoComplete="tel" />
              {error && <MinimalAlert tone="danger">{error}</MinimalAlert>}
              <Style.Actions>
                {skip}
                <MinimalButton onClick={savePhone} disabled={busy || !phone.trim()}>
                  {busy ? 'Saving…' : 'Save and continue'}
                </MinimalButton>
              </Style.Actions>
            </Style.Fields>
          )}
        </MinimalCard>
      </Style.Wrap>
    </Screen>
  )
}
